import { AnnouncementType, EntityType } from '@crew/enums/domain'
import { useTranslation } from '@crew/modules/i18n'
import { getDefaultTabValue } from '@crew/utils/enum'
import { CrewAvatarSize } from 'components/elements/crewAvatar/crewAvatar'
import { CrewLink, LinkColor } from 'components/elements/crewLink/crewLink'
import { CrewLoadingText } from 'components/elements/crewLoading/crewLoadingText/crewLoadingText'
import { CrewRelatedItemLink } from 'components/elements/crewRelatedItemLink/crewRelatedItemLink'
import { CrewUserAvatar } from 'components/elements/crewUserAvatar/crewUserAvatar'
import {
  AnnouncementContextMenuType,
  DirectMessageTabs,
  EventDetailTabs,
  TaskDetailListTabs,
} from 'enums/app'
import { FC, memo, useCallback, useState } from 'react'
import { AnnouncementContextMenu } from '../../../anncounementContextMenu/announcementContextMenu'
import { useHasHover } from 'hooks/useHasHover'
import { AnnouncementItem, useUserSetting } from '@crew/states'
import classNames from 'classnames'
import { useInView } from 'react-intersection-observer'
import { useValueChangeEffect } from '@crew/hooks'
import { ADDITIONAL_LOADING_TRIGGER_INTERSECTION_ROOT_MARGIN } from 'configs/constants'
import { Region, SettingKeyType } from '@crew/enums/app'
import FiberManualRecord from '~icons/material-symbols/fiber-manual-record'
import { useGetLookupRelatedItemQuery } from '@crew/apis/lookup/lookupApis'
import { skipToken } from '@reduxjs/toolkit/query'
import { useCrewNavigate } from 'hooks/useCrewNavigate'
import { useUpdateAnnouncementReadMutation } from '@crew/apis/dist/announcement/announcementApis'
import { useShowApiErrors } from 'hooks/useShowApiErrors'

export type AnnouncementListItemProps = {
  item: AnnouncementItem
  id: string
  onAnnouncementInView: (inView: boolean, announcementId: string) => void
  selectedItemId?: string
  setSelectedItemId: (announcementId: string) => void
  onAdditionalLoading: (() => void) | undefined
  container: React.RefObject<HTMLDivElement>
}

export const AnnouncementListItem: FC<AnnouncementListItemProps> = memo(
  (props) => {
    const announcement = props.item

    // お知らせ元のエンティティ情報を取得する
    const getLookupRelatedItemParams =
      announcement.entityType && announcement.entityRecordId
        ? {
            entityType: announcement.entityType,
            id: announcement.entityRecordId,
          }
        : undefined

    const { data: getLookupRelatedItemResult } = useGetLookupRelatedItemQuery(
      getLookupRelatedItemParams ?? skipToken
    )

    const [updateAnnouncementReadMutation] = useUpdateAnnouncementReadMutation()

    const { t } = useTranslation()
    const { navigate } = useCrewNavigate()
    const [showApiErrors] = useShowApiErrors()

    // ユーザー設定からデフォルトのユーザープロファイル地域を取得
    const defaultUserProfileRegion = useUserSetting(
      SettingKeyType.UserProfileRegion,
      Region.Japan.value
    )

    const hasHover = useHasHover()
    const [isHover, setIsHover] = useState<boolean>(false)
    // 上端、下端、メッセージ本体の要素を表示したかどうかのフラグ
    const [isShownTopDiv, setIsShowTopDiv] = useState(false)
    const [isShownBottomDiv, setIsShownBottomDiv] = useState(false)
    const [isShownAnnouncementDiv, setIsShownAnnouncementDiv] = useState(false)

    const isSelected = props.selectedItemId === props.item.id

    const visibleActionMenu = hasHover
      ? isHover //ホバー有効ならホバー状態であること
      : isSelected! //ホバー無効なら選択状態であるこ

    // propsの関数は代入して使用（useEffect内で使用時、依存配列にpropsが入ってしまうため）
    const handleAnnouncementInView = props.onAnnouncementInView

    const setSelectedItemId = props.setSelectedItemId

    // マウスホバー時にアクションメニューを表示する
    const handleMouseEnter = useCallback(() => {
      setIsHover(true)
    }, [setIsHover])

    // マウスが離れたらアクションメニューを非表示にする
    const handleMouseLeave = useCallback(() => {
      setIsHover(false)
    }, [setIsHover])

    const handleAnnouncementClick = useCallback(() => {
      setSelectedItemId(announcement.id)

      const itemId = getLookupRelatedItemResult?.relatedItem?.id
      if (itemId === undefined) {
        return
      }

      let url = ''
      // 対象のエンティティの詳細画面に遷移するURLを生成
      switch (announcement.entityType) {
        case EntityType.Task:
          // タスク
          url = `/${EntityType.Task}/${itemId}/${getDefaultTabValue(
            TaskDetailListTabs
          )}`
          break
        case EntityType.Event:
          // イベント
          switch (announcement.announcementType) {
            case AnnouncementType.MeetingStarted:
              // 会議開始のお知らせの場合は、会議の詳細画面に遷移する
              url = `/${EntityType.Event}/${itemId}/${getDefaultTabValue(
                EventDetailTabs
              )}`
              break
            case AnnouncementType.MeetingMinutesGenerationSucceeded:
            case AnnouncementType.MeetingMinutesGenerationFailed:
              // 議事録生成完了/失敗のお知らせの場合は、会議の議事録タブに遷移する
              url = `/${EntityType.Event}/${itemId}/${EventDetailTabs.MeetingMinutes.value}`
              break
          }
          break
        default:
          url = ''
          break
      }

      if (!url) {
        return
      }

      // 既読にする
      try {
        updateAnnouncementReadMutation({
          announcementId: announcement.id,
          isRead: true,
        })
      } catch (err) {
        showApiErrors(err)
      }

      // 対象のエンティティの詳細画面に遷移する
      navigate(url)
    }, [
      announcement.announcementType,
      announcement.entityType,
      announcement.id,
      getLookupRelatedItemResult?.relatedItem?.id,
      navigate,
      setSelectedItemId,
      showApiErrors,
      updateAnnouncementReadMutation,
    ])

    // メッセージの要素が一部でも表示されたらメッセージの表示フラグを更新する
    const { ref: announcementRef } = useInView({
      threshold: 0, // 一部でも表示したときに発火する
      root: props.container.current,
      onChange: (inView) => {
        setIsShownAnnouncementDiv(inView)
        // メッセージの要素が非表示になった場合は、上端・下端の要素も非表示になったとみなす
        if (!inView) {
          setIsShowTopDiv(false)
          setIsShownBottomDiv(false)
        }
      },
    })

    // 上端の要素が表示されたら表示フラグを更新する
    const { ref: topRef } = useInView({
      root: props.container?.current,
      onChange: (inView) => {
        // 上端の要素が非表示になった場合でも、メッセージの要素が表示されている場合は表示したことがあるとみなす
        if (!inView && isShownAnnouncementDiv) {
          return
        }

        setIsShowTopDiv(inView)
      },
    })

    // 下端の要素が表示されたら表示フラグを更新する
    const { ref: bottomRef } = useInView({
      root: props.container?.current,
      onChange: (inView) => {
        // 下端の要素が非表示になった場合でも、メッセージの要素が表示されている場合は表示したことがあるとみなす
        if (!inView && isShownAnnouncementDiv) {
          return
        }
        setIsShownBottomDiv(inView)
      },
    })

    // 表示状態が更新されたらイベントハンドラを呼び、スクロール位置の保持を行う
    useValueChangeEffect(
      () => {
        if (isShownTopDiv && isShownBottomDiv) {
          /* メッセージ表示（上端、下端どちらも表示したことがある） */
          handleAnnouncementInView(true, announcement.id)
        } else if (!isShownTopDiv && !isShownBottomDiv) {
          /* メッセージ非表示（上端、下端どちらも表示したことがない） */
          handleAnnouncementInView(false, announcement.id)
        }
      },
      [
        announcement.id,
        handleAnnouncementInView,
        isShownBottomDiv,
        isShownTopDiv,
      ],
      { isShownTopDiv, isShownBottomDiv },
      false,
      (prev, next) =>
        prev.isShownTopDiv === next.isShownTopDiv &&
        prev.isShownBottomDiv === next.isShownBottomDiv
    )

    // アイテムが表示領域に近づいたら追加ロードイベントハンドラを呼ぶ
    const { ref: loadingTriggerRef } = useInView({
      rootMargin: ADDITIONAL_LOADING_TRIGGER_INTERSECTION_ROOT_MARGIN, // 表示領域に「近づいた」をトリガーにするため、領域の外側にマージンを付与する
      root: props.container.current,
      onChange: (inView) => {
        // このイベントは、アイテムと判定領域の重なり方の割合がthreshold(デフォルト: 0)を越えた場合に発火する。
        // 近づいた場合と離れた場合のどちらも発火するが、inViewの値で方向を判定することができる。
        //   近づいた場合: true 離れた場合: false
        if (inView) {
          // アイテムが近づいてきた場合、追加読込を行う
          props.onAdditionalLoading?.()
        }
      },
    })

    return (
      <div
        id={props.id}
        className={classNames('flex flex-col p-2.5 crew-border-gray relative', {
          'crew-highlighted-message': isSelected, // 対象メッセージがスレッド表示の対象として選択されている場合ハイライトする
          'crew-active-message': !isSelected && isHover, // ホバー表示、ただし選択されていたらそちらを優先する
        })}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* メッセージ上端の要素。表示判定に使用するのみで画面には何も表示しない */}
        <div ref={topRef} className="-z-10"></div>
        {/** 未読メッセージの場合は未読マークを表示する */}
        {!announcement.isRead && (
          <FiberManualRecord
            width={15}
            height={15}
            className="crew-badge-unread-message absolute top-0 left-0 m-0.5"
          />
        )}
        {props.onAdditionalLoading && (
          <div
            ref={loadingTriggerRef}
            className="absolute left-0 top-0 right-0 bottom-0 -z-10"
          />
        )}
        <div
          className={classNames('flex flex-row gap-2.5')}
          onClick={handleAnnouncementClick}
          ref={announcementRef}
        >
          {/** アイコン */}
          <div className="flex-shrink-0">
            <CrewUserAvatar
              userId={announcement.createdBy.id}
              displayName={announcement.createdBy.displayName}
              size={CrewAvatarSize.base}
              cacheValue={
                announcement.createdBy.id + announcement.createdBy.version
              }
            />
          </div>
          {/** メッセージ部分 */}
          <div className="flex flex-col gap-1">
            <div className="flex flex-row gap-2 truncate">
              {/** ユーザー名と投稿日 */}
              {/** ユーザー名 */}
              <CrewLink
                to={`/${EntityType.User}/${
                  announcement.createdBy.id
                }/${getDefaultTabValue(DirectMessageTabs)}`}
                className="text-sm font-bold crew-text-gray-4 hover:text-crew-gray-500 hover:dark:text-crew-gray-400"
              >
                {announcement.createdBy.displayName ?? (
                  <CrewLoadingText className="h-full inline-block" />
                )}
              </CrewLink>
              {/** 投稿日時 */}
              <span className="text-sm crew-text-gray-5">
                {t('format.timestamp', {
                  // チャットメッセージは更新日時を使用するが、お知らせは登録された日時を使用する
                  value: announcement.createdAt,
                  timeZone: defaultUserProfileRegion,
                })}
              </span>
            </div>
            {/** 関連先 */}
            <span className="text-sm crew-text-gray-5 truncate">
              {getLookupRelatedItemResult &&
                getLookupRelatedItemResult?.relatedItem?.entityRecordId &&
                getLookupRelatedItemResult?.relatedItem.entityType && (
                  <>
                    <CrewRelatedItemLink
                      entityType={
                        getLookupRelatedItemResult?.relatedItem?.entityType
                      }
                      id={
                        getLookupRelatedItemResult?.relatedItem?.entityRecordId
                      }
                      color={LinkColor.Gray}
                      stopPropagation={true}
                    />
                    &nbsp;/&nbsp;
                  </>
                )}
              <CrewRelatedItemLink
                entityType={announcement.entityType}
                id={announcement.entityRecordId}
                color={LinkColor.Gray}
                stopPropagation={true}
                showAccessDeniedMessage={true}
              />
            </span>
            {/** メッセージ本文 */}
            <span>
              {t(`message.announcement.${announcement.announcementType}`)}
            </span>
          </div>
        </div>
        {/* メッセージ下端の要素。表示判定に使用するのみで画面には何も表示しない */}
        <div ref={bottomRef} className="-z-10"></div>

        {/** アクションメニュー */}
        <div className="absolute top-0 right-0">
          <AnnouncementContextMenu
            announcementId={announcement.id}
            visible={visibleActionMenu}
            contextMenuType={AnnouncementContextMenuType.ForSingle}
            showBorder={true}
          />
        </div>
      </div>
    )
  }
)
